import { useState, useEffect, useCallback } from "react";
import {
  IoAddOutline,
  IoImageOutline,
  IoBuildOutline,
  IoTrashOutline,
  IoEllipsisHorizontalOutline,
} from "react-icons/io5";

import Toaster from "../../../../components/common/Toaster";
import Dropdown from "../../../../components/common/Dropdown";
import Modal from "../../../../components/common/Modal";

import IdentificationForm from "./components/IdentificationForm";
import IdentificationImages from "./components/IdentificationImages";

import accountService from "../../../../services/accountService";
import DeleteIdentification from "./components/DeleteIdentification";

const Identifications = ({ id }) => {
  const [identification, setIdentification] = useState(null);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showImages, setShowImages] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const actions = (item) => [
    [
      {
        name: <span className="text-gray-400">View Images</span>,
        icon: <IoImageOutline className="mr-3 text-green-400" />,
        action: () => handleImages(item),
      },
    ],
    [
      {
        name: <span className="text-gray-400">Edit ID</span>,
        icon: <IoBuildOutline className="mr-3 text-blue-400" />,
        action: () => handleModal(item),
      },
    ],
    [
      {
        name: <span className="text-red-500">Delete ID</span>,
        icon: <IoTrashOutline className="mr-3 text-red-500" />,
        action: () => handleDelete(item),
      },
    ],
  ];

  const handleImages = (item) => {
    const result = item ?? null;
    setIdentification(result);
    setShowImages(!showImages);
  };

  const handleModal = (item) => {
    const result = item ?? null;
    setIdentification(result);
    setShowForm(!showForm);
  };

  const handleDelete = (item) => {
    const result = item ?? null;
    setIdentification(result);
    setShowDelete(!showDelete);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setRefresh(!refresh);
  };

  const handleSuccessDelete = () => {
    setShowDelete(false);
    setRefresh(!refresh);
  };

  const fetchIdentifications = useCallback(
    async (mounted) => {
      try {
        setIsLoading(true);

        const result = await accountService.getAccountIdentityDocumentsAsync(
          id
        );

        if (mounted) {
          setData(result);
          setIsLoading(false);
        }
      } catch (ex) {
        Toaster.Error("Something went wrong.");
        setIsLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    let mounted = true;

    fetchIdentifications(mounted);

    return () => {
      mounted = false;
    };
  }, [fetchIdentifications, refresh]);

  return (
    <div className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500">Identification Cards</p>
          <span className="text-xs text-gray-400">
            List of Identification Cards
          </span>
        </div>
        {data.length <= 4 && (
          <IoAddOutline
            className="text-blue-500 text-lg cursor-pointer"
            onClick={() => handleModal()}
          />
        )}
      </div>
      <div className="mt-6 mb-4">
        {isLoading && (
          <div className="flex-1 space-y-3 py-1 animate-pulse">
            <div className="h-4 bg-gray-100 rounded w-3/5"></div>
            <div className="h-4 bg-gray-100 rounded w-5/5"></div>
            <div className="h-4 bg-gray-100 rounded w-4/5"></div>
            <div className="h-4 bg-gray-100 rounded w-2/5"></div>
          </div>
        )}
        {!isLoading && data.length > 0 && (
          <ul className="border border-gray-200 rounded-md divide-y divide-gray-200">
            {data.map((item, index) => (
              <li key={index} className="flex justify-between pl-3 pr-4 py-3">
                <div>
                  <p className="text-gray-400 text-sm">{item.number}</p>
                  <span className="text-gray-400 text-xs">
                    {item.alternate_type_name}
                  </span>
                </div>
                <Dropdown
                  items={actions(item)}
                  icon={
                    <IoEllipsisHorizontalOutline className="text-gray-400 -mx-3" />
                  }
                />
              </li>
            ))}
          </ul>
        )}
        {!isLoading && data.length === 0 && (
          <div className="text-center bg-gray-50 p-8 rounded-xl mb-2">
            <div className="flex justify-center">
              <div>
                <IoImageOutline className="text-3xl inline text-gray-300" />
                <p className="text-gray-400 text-sm mt-3">Nothing to show</p>
                <span className="text-gray-400 text-xs">No data available</span>
              </div>
            </div>
          </div>
        )}
      </div>
      <p className="text-gray-400 text-xs text-right mt-5">Maximum of 5 IDs</p>
      <Modal
        title="Card Information"
        isOpen={showForm}
        onClose={() => handleModal()}
        borderTop
      >
        <IdentificationForm
          id={id}
          data={identification}
          isLoading={isFormLoading}
          setLoading={setIsFormLoading}
          onSuccess={handleSuccess}
        />
      </Modal>
      <Modal
        title={
          identification && (
            <div>
              <p className="text-gray-500 text-sm">{identification.number}</p>
              <span className="text-gray-400 text-xs">
                {identification.alternate_type_name}
              </span>
            </div>
          )
        }
        isOpen={showImages}
        onClose={() => handleImages()}
        borderTop
      >
        <IdentificationImages id={identification && identification.id} />
      </Modal>
      <Modal
        isOpen={showDelete}
        isLoading={isFormLoading}
        onClose={() => handleDelete()}
      >
        <DeleteIdentification
          data={identification}
          isLoading={isFormLoading}
          setLoading={setIsFormLoading}
          onClose={() => handleDelete()}
          onSuccess={handleSuccessDelete}
        />
      </Modal>
    </div>
  );
};

export default Identifications;
